import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';
import MedicalDropdown from '../../Dropdown/MedicalDropdown';
import api from '../../../../../api';

// AttendanceAddModal component for adding or editing attendance records in a modal
const AttendanceAddModal = ({
  show,
  onClose,
  formData,
  onChange,
  onSave,
  isEditMode,
  isLoading,
}) => {
  const [employees, setEmployees] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [validatedFields, setValidatedFields] = useState({
    emp_id: false,
    todaystatus: false,
    area_id: false,
    location_id: false,
    medical_center_id: false,
  });
  const [formErrors, setFormErrors] = useState({
    emp_id: false,
    todaystatus: false,
    area_id: false,
    location_id: false,
    medical_center_id: false,
  });
  const [selectionType, setSelectionType] = useState('location');

  // Session data
  const sessionData = {
    roleName: sessionStorage.getItem('Role_Name'),
    medicalCenterId: sessionStorage.getItem('Medical_Center_Id'),
    medicalCenterName: sessionStorage.getItem('Medical_Center_Name'),
  };

  const isResourceManager = sessionData.roleName === 'EMS Resource Manager';
  const isSpecificMedicalCenter =
    isResourceManager &&
    sessionData.medicalCenterId &&
    sessionData.medicalCenterId !== '0' &&
    sessionData.medicalCenterId !== '[]';

  // Set initial selection type
  useEffect(() => {
    if (isEditMode) {
      if (formData.medical_center_id && formData.medical_center_id !== '0') {
        setSelectionType('medical');
      } else if (formData.location_id && formData.location_id !== '0') {
        setSelectionType('location');
      }
    } else if (isSpecificMedicalCenter) {
      setSelectionType('medical');
      onChange({ target: { name: 'medical_center_id', value: sessionData.medicalCenterId } });
      onChange({ target: { name: 'location_id', value: '' } });
      onChange({ target: { name: 'area_id', value: '' } });
    } else {
      setSelectionType('location');
    }
  }, [isEditMode, formData.medical_center_id, formData.location_id, isSpecificMedicalCenter, onChange]);

  // Reset validation and errors when modal is shown
  useEffect(() => {
    if (show) {
      setValidatedFields({
        emp_id: isEditMode,
        todaystatus: isEditMode,
        area_id: isEditMode && selectionType === 'location',
        location_id: isEditMode && selectionType === 'location',
        medical_center_id: isEditMode && selectionType === 'medical',
      });
      setFormErrors({
        emp_id: false,
        todaystatus: false,
        area_id: false,
        location_id: false,
        medical_center_id: false,
      });

      if (isEditMode && formData.emp_id) {
        fetchEmployeeData(formData.emp_id);
      }
    }
  }, [show, isEditMode, formData.emp_id, selectionType]);

  // Fetch all employees on component mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/ems/employees');
        const data = response.data;

        // CASE 1: backend returns an array already
        if (Array.isArray(data)) {
          setEmployees(data);
        }
        // CASE 2: backend returns { data: [...] }
        else if (Array.isArray(data?.data)) {
          setEmployees(data.data);
        }
        // CASE 3: backend returns { employees: [...] }
        else if (Array.isArray(data?.employees)) {
          setEmployees(data.employees);
        }
        // OTHERWISE set empty
        else {
          console.warn("Unexpected employee data format:", data);
          setEmployees([]);
        }

      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch individual employee data by emp_id
  const fetchEmployeeData = async (empId) => {
    try {
      const selected = Array.isArray(employees)
        ? employees.find(emp => emp.emp_id === empId)
        : null;
      setEmployeeData(selected || null);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setEmployeeData(null);
    }
  };

  // Fetch employee data when emp_id changes in add mode
  useEffect(() => {
    if (!isEditMode && formData.emp_id) {
      fetchEmployeeData(formData.emp_id);
    }
  }, [formData.emp_id, isEditMode, employees]);

  // Handle location dropdown changes
  const handleLocationChange = (e) => {
    onChange(e);
    onChange({ target: { name: 'area_id', value: '' } });
    setFormErrors(prev => ({ ...prev, area_id: false, location_id: false }));
  };

  // Handle medical center change
  const handleMedicalCenterChange = (e) => {
    onChange(e);
    setFormErrors(prev => ({ ...prev, medical_center_id: false }));
  };

  // Handle selection type change
  const handleSelectionTypeChange = (e) => {
    const type = e.target.value;
    if (isSpecificMedicalCenter && type === 'location') return;
    setSelectionType(type);
    onChange({ target: { name: 'location_id', value: '' } });
    onChange({ target: { name: 'area_id', value: '' } });
    onChange({ target: { name: 'medical_center_id', value: '' } });
    setFormErrors(prev => ({
      ...prev,
      location_id: false,
      area_id: false,
      medical_center_id: false,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = {
      emp_id: !formData.emp_id,
      todaystatus: !formData.todaystatus,
      location_id: selectionType === 'location' && !formData.location_id,
      area_id: selectionType === 'location' && !formData.area_id,
      medical_center_id: selectionType === 'medical' && !formData.medical_center_id,
    };

    setValidatedFields({
      emp_id: true,
      todaystatus: true,
      location_id: selectionType === 'location',
      area_id: selectionType === 'location',
      medical_center_id: selectionType === 'medical',
    });

    setFormErrors(errors);

    const hasErrors = Object.values(errors).some(error => error);
    if (!hasErrors) {
      onSave();
    }
  };

  // Handle blur event for form fields
  const handleBlur = (field) => {
    if (!isEditMode || formData[field] !== formData[field]) {
      setValidatedFields(prev => ({ ...prev, [field]: true }));
      setFormErrors(prev => ({ ...prev, [field]: !formData[field] }));
    }
  };

  // Determine if validation error should be shown
  const shouldShowValidation = (field) => validatedFields[field] && formErrors[field];

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">{isEditMode ? 'Edit Attendance' : 'Add Attendance'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Employee ID <span className="text-danger">*</span></label>
                  <input
                    list="employeeList"
                    name="emp_id"
                    className={`form-control ${shouldShowValidation('emp_id') ? 'is-invalid' : ''}`}
                    value={formData.emp_id || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('emp_id')}
                    disabled={isEditMode}
                    autoComplete="off"
                  />
                  <datalist id="employeeList">
                  {Array.isArray(employees) && employees.map(emp => (
                      <option key={emp.id} value={emp.emp_id}>
                        {`${emp.emp_id} - ${emp.first_name} ${emp.last_name}`}
                      </option>
                    ))}
                  </datalist>
                  {shouldShowValidation('emp_id') && (
                    <div className="invalid-feedback">Employee ID is required</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={employeeData ? `${employeeData.first_name} ${employeeData.last_name}` : ''}
                    disabled
                  />
                </div>
              </div>

              {!isEditMode && (
                <div className="col-12 mb-3">
                  <label className="me-3">
                    Choose Location or Medical Center <span className="text-danger">*</span>
                  </label>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="selectionType"
                      id="locationRadio"
                      value="location"
                      checked={selectionType === 'location'}
                      onChange={handleSelectionTypeChange}
                      disabled={isSpecificMedicalCenter}
                    />
                    <label className="form-check-label" htmlFor="locationRadio">
                      Location
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="selectionType"
                      id="medicalRadio"
                      value="medical"
                      checked={selectionType === 'medical'}
                      onChange={handleSelectionTypeChange}
                    />
                    <label className="form-check-label" htmlFor="medicalRadio">
                      Medical Center
                    </label>
                  </div>
                </div>
              )}

              {selectionType === 'location' ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label>Location <span className="text-danger">*</span></label>
                    <LocationDropdown
                      value={formData.location_id || ''}
                      onChange={handleLocationChange}
                      onBlur={() => handleBlur('location_id')}
                      className={shouldShowValidation('location_id') ? 'is-invalid' : ''}
                    />
                    {shouldShowValidation('location_id') && (
                      <div className="invalid-feedback">Location is required</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Area <span className="text-danger">*</span></label>
                    <AreaDropdown
                      locationId={formData.location_id || ''}
                      value={formData.area_id || ''}
                      onChange={onChange}
                      onBlur={() => handleBlur('area_id')}
                      className={shouldShowValidation('area_id') ? 'is-invalid' : ''}
                      disabled={!formData.location_id}
                    />
                    {shouldShowValidation('area_id') && (
                      <div className="invalid-feedback">Area is required</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="col-md-6 mb-3">
                  <label>Medical Center <span className="text-danger">*</span></label>
                  <MedicalDropdown
                    value={
                      formData.medical_center_id ||
                      (isSpecificMedicalCenter ? sessionData.medicalCenterId : '')
                    }
                    onChange={handleMedicalCenterChange}
                    onBlur={() => handleBlur('medical_center_id')}
                    className={shouldShowValidation('medical_center_id') ? 'is-invalid' : ''}
                    roleName={sessionData.roleName}
                    disabled={isSpecificMedicalCenter}
                  />
                  {isSpecificMedicalCenter && (
                    <small className="form-text text-muted">
                      {sessionData.medicalCenterName}
                    </small>
                  )}
                  {shouldShowValidation('medical_center_id') && (
                    <div className="invalid-feedback">Medical Center is required</div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <label>Status <span className="text-danger">*</span></label>
                <select
                  name="todaystatus"
                  className={`form-control form-select ${shouldShowValidation('todaystatus') ? 'is-invalid' : ''}`}
                  value={formData.todaystatus || ''}
                  onChange={onChange}
                  onBlur={() => handleBlur('todaystatus')}
                >
                  <option value="">Select Status</option>
                  <option value="1">Present</option>
                  <option value="0">Absent</option>
                </select>
                {shouldShowValidation('todaystatus') && (
                  <div className="invalid-feedback">Status is required</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                    {isEditMode ? 'Updating...' : 'Saving...'}
                  </>
                ) : isEditMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AttendanceAddModal;